import { i18n } from '@/locale/i18n'
import React, { useEffect } from 'react'
import { Crevron } from '@/assets/icons'
import './MarketListHeader.scss'

const MarketListHeader = ({
  numberOfActiveBets = 0,
  isLoading,
  countLoading = false,
  filters,
  title = '',
  /** Rendered right after the title — e.g. the clear-search button. */
  titleAction = null,
  leftContent,
  onTitleClick,
  className = '',
}) => {
  // Add shimmer animation styles if not already present
  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('shimmer-styles')) {
      const style = document.createElement('style')
      style.id = 'shimmer-styles'
      style.textContent = `
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  // Shimmer effect component
  const ShimmerEffect = ({ className = '', children }) => (
    <div className={`relative overflow-hidden ${className}`}>
      {children}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-3/30 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)',
        }}
      />
    </div>
  )

  return (
    <>
      <div className={`flex flex-row justify-between items-center py-6 !px-4 md:!px-[30px] ${className}`}>
        {isLoading ? (
          <>
            {/* Mobile loading state */}
            <div className="flex flex-row justify-between items-center w-full md:hidden">
              {/* Results text skeleton */}
              <ShimmerEffect>
                <div className="animate-pulse h-5 bg-surface-2 rounded w-24"></div>
              </ShimmerEffect>

              {/* Two circular button skeletons */}
              <div className="flex gap-2">
                <ShimmerEffect className="rounded-full">
                  <div className="animate-pulse h-10 w-10 bg-surface-2 rounded-full"></div>
                </ShimmerEffect>
                <ShimmerEffect className="rounded-full">
                  <div className="animate-pulse h-10 w-10 bg-surface-2 rounded-full"></div>
                </ShimmerEffect>
              </div>
            </div>

            {/* Desktop loading state */}
            <div className="hidden md:!flex md:flex-row md:justify-between md:items-center md:w-full">
              {/* Title shimmer */}
              <ShimmerEffect>
                <div className="animate-pulse h-8 bg-surface-2 rounded w-32"></div>
              </ShimmerEffect>

              {/* Results count shimmer */}
              <div className="flex gap-1 pt-1">
                <ShimmerEffect>
                  <div className="animate-pulse h-10 bg-surface-2 rounded w-28"></div>
                </ShimmerEffect>
                <ShimmerEffect>
                  <div className="animate-pulse h-10 bg-surface-2 rounded w-28"></div>
                </ShimmerEffect>
                <ShimmerEffect>
                  <div className="animate-pulse h-10 bg-surface-2 rounded w-28"></div>
                </ShimmerEffect>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-row gap-3 items-center flex-shrink-0">
              {leftContent}
              {title && onTitleClick ? (
                <h3
                  className="text-2xl text-Neutral-6 leading-[133%] font-semibold flex items-center gap-1 cursor-pointer"
                  onClick={onTitleClick}>
                  {title}
                  <Crevron width="12" height="7" fillcolor="var(--color-Neutral-5)" />
                </h3>
              ) : title ? (
                <h3 className="text-2xl text-Neutral-6 leading-[133%] font-semibold">{title}</h3>
              ) : null}
              {title && titleAction}
              {countLoading ? (
                /* The count refetches on every tab/category change — shimmer instead of popping in/out */
                <ShimmerEffect className="rounded-full">
                  <div className="animate-pulse h-6 w-10 bg-surface-2 rounded-full"></div>
                </ShimmerEffect>
              ) : numberOfActiveBets != null ? (
                // Neutral on purpose: the count is metadata — brand color marks selection, not info
                <div className="text-sm text-Neutral-6 rounded-full px-2 py-0.5 bg-surface-2 border border-solid border-Neutral-3">
                  {numberOfActiveBets}
                </div>
              ) : null}
            </div>
            <div className="flex gap-4">{filters}</div>
          </>
        )}
      </div>
    </>
  )
}

export default MarketListHeader
